import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatToDayMonthYear } from "../../utils/TextUtils.js";

function FarmerView() {
    const [farmer, setFarmer] = useState(null);
    const [milkCollections, setMilkCollections] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalLitres, setTotalLitres] = useState(0);
    const [totalDebt, setTotalDebt] = useState(0);
    const [debts, setDebts] = useState([]);
    const [duePayout, setDuePayout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState("");
    const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
    const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
    const [payments, setPayments] = useState([]);
    const [currentPaymentPage, setCurrentPaymentPage] = useState(1);
    const [totalPaymentPages, setTotalPaymentPages] = useState(1);
    const [totalPayments, setTotalPayments] = useState(0);
    const [downloadingDebts, setDownloadingDebts] = useState(false);
    const [downloadingMilkCollections, setDownloadingMilkCollections] = useState(false);
    const [downloadingPayments, setDownloadingPayments] = useState(false);

    // Pagination (Milk Collections)
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Pagination (Debts)
    const [currentDebtPage, setCurrentDebtPage] = useState(1);
    const [totalDebtPages, setTotalDebtPages] = useState(1);

    // Filters (Milk Collection)
    const [filterDate, setFilterDate] = useState("");
    const [filterMonth, setFilterMonth] = useState("");

    // Filters (Debts)
    const [filterDebtDate, setFilterDebtDate] = useState("");
    const [filterDebtMonth, setFilterDebtMonth] = useState("");

    const perPage = 10;
    const navigate = useNavigate();
    const { id } = useParams();
    const userRole = localStorage.getItem("userRole");
    const canEdit = allowedToView(userRole, ["admin", "superadmin"]);
    const token = localStorage.getItem("access_token");

    // Fetch farmer details
    useEffect(() => {
        const fetchFarmer = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}/farmers/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setFarmer(data);
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "Farmer not found." });
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch farmer.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching the farmer." });
            } finally {
                setLoading(false);
            }
        };
        fetchFarmer();
    }, [id, navigate]);

    useEffect(() => {
        fetchMilkCollections();
    }, [currentPage, filterDate, filterMonth]);

    useEffect(() => {
        fetchDebts();
    }, [currentDebtPage, filterDebtDate, filterDebtMonth]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterDate, filterMonth]);

    useEffect(() => {
        setCurrentDebtPage(1);
    }, [filterDebtDate, filterDebtMonth]);

    useEffect(() => {
        fetchPayments();
    }, [currentPaymentPage]);

    useEffect(() => {
        setCurrentPaymentPage(1);
    }, []);

    useEffect(() => {
        fetchPayouts();
    }, []);

    const fetchMilkCollections = async () => {
        try {
            let url = `${API_URL}/milk-collections?farmer_id=${id}&page=${currentPage}&limit=${perPage}`;
            if (filterDate) url += `&date=${filterDate}`;
            if (filterMonth) url += `&month=${filterMonth}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setMilkCollections(data.data.data || []);
                setTotalRevenue(data.total_revenue || 0);
                setTotalLitres(data.total_litres || 0);
                setTotalPages(data.data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch milk collections");
            }
        } catch (error) {
            setNotification({ type: "danger", message: "An error occurred while fetching milk collections." });
        }
    };

    const fetchDebts = async () => {
        try {
            let url = `${API_URL}/debts?farmer_id=${id}&page=${currentDebtPage}&limit=${perPage}`;
            if (filterDebtDate) url += `&date_taken=${filterDebtDate}`;
            if (filterDebtMonth) url += `&month=${filterDebtMonth}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setDebts(data.data.data || []);
                setTotalDebt(data.total_debt);
                setTotalDebtPages(data.data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch debts");
            }
        } catch (error) {
            setNotification({ type: "danger", message: "An error occurred while fetching debts." });
        }
    };

    const fetchPayouts = async () => {

        setLoading(true);
        try {
            let url = `${API_URL}/milk-payments/farmer/${id}/due`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setDuePayout(data); // Set the due payout data
            } else if (response.status === 401) {
                navigate('/logout');
            } else if (response.status == 404) {
                // Silence is golden

            } else {
                throw new Error("Failed to fetch payouts");
            }
        } catch (error) {
            setNotification({ type: "danger", message: "An error occurred while fetching payouts." });
        } finally {
            setLoading(false);
        }
    };

    const markAsPaid = async () => {
        try {
            let url = `${API_URL}/milk-payments/pay/${duePayout?.id}`;
            const response = await fetch(url, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                setNotification({ type: "success", message: "Updated successfully" });
                setDuePayout(null);
                await fetchPayouts();
                await fetchPayments();
            } else if (response.status === 401) {
                navigate("/logout")
            } else {
                setNotification({ type: "danger", message: "There was an error making the update." })
            }
            setConfirmDialogOpen(false);
        } catch (error) {
            console.log(error)
            setNotification({ type: "danger", message: "An error occurred while updating the payment." });
            setConfirmDialogOpen(false);
        }
    };

    const fetchPayments = async () => {
        try {
            const url = `${API_URL}/milk-payments/farmer/${id}?page=${currentPaymentPage}&limit=${perPage}`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setPayments(data.data || []);
                setTotalPaymentPages(data.last_page || 1);
                setTotalPayments(data.total || 0);
            } else if (response.status === 401) {
                navigate('/logout');
            } else {
                throw new Error("Failed to fetch payments");
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: "An error occurred while fetching payments."
            });
        }
    };

    const handleConfirmation = async () => {
        try {
            switch (confirmAction) {
                case "markAsPaid":
                    await markAsPaid();
                    break;
            }
        } catch (error) {
            setNotification({ type: "danger", message: "There was an error handling confirmation" })
        }
    }

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleFilterDateChange = (e) => setFilterDate(e.target.value);
    const handleFilterMonthChange = (e) => setFilterMonth(e.target.value);
    const handleFilterDebtDateChange = (e) => setFilterDebtDate(e.target.value);
    const handleFilterDebtMonthChange = (e) => setFilterDebtMonth(e.target.value);

    const handleConfirmPaymentStatus = () => {
        setConfirmAction("markAsPaid");
        setConfirmDialogTitle("Confirm Payment");
        setConfirmDialogMessage(`Are you sure you want to mark KES. ${duePayout.amount_to_pay} as paid to ${farmer?.first_name} ${farmer.middle_name || ""} ${farmer.last_name || ""}?`);
        setConfirmDialogOpen(true);
    };

    // Download reports for payments, milk collections, and debts
    const downloadPaymentsPDF = async () => {
        try {
            setDownloadingPayments(true);
            const response = await fetch(`${API_URL}/milk-payments/farmer/${id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `payments_${farmer.farmer_number}_${new Date().toISOString().slice(0, 10)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: "Farmer not found." });
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch farmer.");
            }
            setDownloadingPayments(false);
        } catch (error) {
            setNotification({ type: "danger", message: "Failed to download payments PDF" });
            setDownloadingPayments(false);
        }
    };

    const downloadMilkCollectionsPDF = async () => {
        try {
            // Validate month is selected
            if (!filterMonth) {
                window.scrollTo({ top: 0, behavior: "smooth" });

                setNotification({
                    type: "danger",
                    message: "Please select a month before downloading"
                });
                return;
            }
            setDownloadingMilkCollections(true);

            let url = `${API_URL}/milk-collections/export?farmer_id=${id}`;
            if (filterDate) url += `&date=${filterDate}`;
            if (filterMonth) url += `&month=${filterMonth}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `milk_collections_${farmer.farmer_number}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                throw new Error("Failed to download milk collections PDF");
            }
            setDownloadingMilkCollections(false);
        } catch (error) {
            setNotification({ type: "danger", message: "Failed to download milk collections PDF" });
            setDownloadingMilkCollections(false);
        }
    };

    const downloadDebtsPDF = async () => {
        try {
            setDownloadingDebts(true);
            let url = `${API_URL}/debts/export?farmer_id=${id}`;
            if (filterDebtMonth) url += `&month=${filterDebtMonth}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `debts_${farmer.farmer_number}_${filterDebtMonth ? filterDebtMonth : 'all'}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                throw new Error("Failed to download debts PDF");
            }
            setDownloadingDebts(false);
        } catch (error) {
            setNotification({
                type: "danger",
                message: "Failed to download debts PDF: " + error.message
            });
            setDownloadingDebts(false);
        }
    };

    if (loading) return <p>Loading...</p>;
    if (!farmer) return <p>No farmer data available.</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Farmer Details</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/farmers" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                        {canEdit && (
                            <>
                                &nbsp;&nbsp;
                                <Link to={`/dashboard/farmers/${farmer.id}/update`} className="button primary-bg">
                                    <i className="fa fa-pencil" aria-hidden="true"></i> &nbsp; Edit
                                </Link>
                            </>
                        )}
                    </p>
                </div>
            </nav>

            {notification.message && (
                <Notification type={notification.type} message={notification.message} onClose={closeNotification} />
            )}

            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                theme="is-primary"
                title={confirmDialogTitle}
                message={confirmDialogMessage}
                onConfirm={handleConfirmation}
                onCancel={() => setConfirmDialogOpen(false)}
            />

            {/* Farmer Details Box */}
            <div className="box">
                <div className="columns">
                    {/* Farmer Details Column */}
                    <div className="column">
                        <div className="field">
                            <label className="label">Farmer Number</label>
                            <div>{farmer.farmer_number || "N/A"}</div>
                        </div>
                        <div className="field">
                            <label className="label">Full Name</label>
                            <div>{`${farmer.first_name} ${farmer.middle_name || ""} ${farmer.last_name}`}</div>
                        </div>
                        <div className="field">
                            <label className="label">Phone Number</label>
                            <div>{farmer.phone_number || "N/A"}</div>
                        </div>
                        <div className="field">
                            <label className="label">ID Number</label>
                            <div>{farmer.id_number || "N/A"}</div>
                        </div>
                        <div className="field">
                            <label className="label">Collection Route</label>
                            <div>{farmer.collection_route?.name || "N/A"}</div>
                        </div>
                    </div>

                    {/* Additional Farmer Details Column */}
                    <div className="column">
                        <div className="field">
                            <label className="label">Account Type</label>
                            <div>{farmer.account_type || "N/A"}</div>
                        </div>

                        <div className="field">
                            <label className="label">Account Number</label>
                            <div>{farmer.account_number || "N/A"}</div>
                        </div>
                        
                        <div className="field">
                            <label className="label">Created At</label>
                            <div>{new Date(farmer.created_at).toLocaleString()}</div>
                        </div>
                        <div className="field">
                            <label className="label">Updated At</label>
                            <div>{farmer.updated_at ? new Date(farmer.updated_at).toLocaleString() : "N/A"}</div>
                        </div>
                    </div>

                    {/* Payout Details Column */}
                    <div className="column">
                        <label className="label">Payouts</label>
                        <div className="box">
                            {duePayout ? (
                                <>
                                    <div className="field">

                                        <div>
                                            <strong>{duePayout.payment_period?.name || "N/A"}</strong>
                                        </div>
                                    </div>
                                    <div className="field">
                                        <label className="label">Balace (KES): &nbsp;
                                            <span style={{ fontSize: "24px", fontWeight: "bold" }}>
                                                {duePayout.amount_to_pay || "N/A"}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="field">
                                        <label className="label">Status: {duePayout.status || "N/A"}</label>

                                    </div>
                                    <div className="field">
                                        <label className="label">Period Status: {duePayout.payment_period?.is_closed ? "Closed" : "Open"} </label>

                                    </div>
                                    <br />
                                    {duePayout.amount_to_pay > 0 && duePayout.status === "Unpaid" && !duePayout.payment_period?.is_closed && canEdit && (
                                        <button className="button is-success" onClick={() => handleConfirmPaymentStatus()}>
                                            <span className="icon">
                                                <i className="fas fa-money-bill-wave"></i>
                                            </span>
                                            <span>Pay Kes. {duePayout.amount_to_pay}</span>
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {loading ? (<div>Loading ...</div>) : (<div>No Due Payment Found for this farmer</div>)}
                                </>

                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Payments */}
            {payments.length > 0 && (<div className="box">
                <h2 className="title is-5">Payment History</h2>

                <div className="notification is-light is-success">
                    <div className="columns is-vcentered">
                        <div className="column">
                            <strong>Total Payments:</strong>
                            <span className="is-size-6 has-text-weight-bold">
                                &nbsp; {totalPayments}
                            </span>
                        </div>
                        <div className="column has-text-right">
                            <button className={`button is-info is-small  ${downloadingPayments ? 'is-loading' : ''}`} onClick={downloadPaymentsPDF}>
                                <span className="icon">
                                    <i className="fas fa-file-pdf"></i>
                                </span>
                                <span>Download Payments</span>
                            </button>
                        </div>
                    </div>
                </div>


                <table className="table is-fullwidth">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Payment Period</th>
                            <th>Amount Paid (KES)</th>
                            <th>Payment Date</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.length ? payments.map((payment, index) => (
                            <tr key={payment.id}>
                                <td>{(currentPaymentPage - 1) * perPage + index + 1}</td>
                                <td>{payment.payment_period?.name || 'N/A'}</td>
                                <td>{payment?.amount_paid || '0.00'}</td>
                                <td>{payment.paid_at ? formatToDayMonthYear(payment.paid_at) : 'N/A'}</td>
                                <td>{payment.status || 'N/A'}</td>
                            </tr>
                        )) : <tr><td colSpan="6">No payment history found.</td></tr>}
                    </tbody>
                </table>

                {/* Pagination for Payments */}
                <nav className="pagination">
                    <button
                        onClick={() => setCurrentPaymentPage(currentPaymentPage - 1)}
                        disabled={currentPaymentPage === 1}
                    >
                        Previous
                    </button>
                    <span>Page {currentPaymentPage} of {totalPaymentPages}</span>
                    <button
                        onClick={() => setCurrentPaymentPage(currentPaymentPage + 1)}
                        disabled={currentPaymentPage === totalPaymentPages}
                    >
                        Next
                    </button>
                </nav>
            </div>
            )}

            <div className="box">
                <h2 className="title is-5">Milk Collections</h2>
                {milkCollections.length > 0 && (
                    <div className="notification is-light is-success">
                        <div className="columns is-vcentered">
                            <div className="column">
                                <div className="columns">
                                    <div className="column">
                                        <strong>Total Litres:</strong>
                                        <span className="is-size-4 has-text-weight-bold">
                                            &nbsp; {totalLitres ? Number(totalLitres).toFixed(2) : '0.00'} L
                                        </span>
                                    </div>
                                    <div className="column">
                                        <strong>Total Income (KES):</strong>
                                        <span className="is-size-4 has-text-weight-bold">
                                            &nbsp; {totalRevenue ? Number(totalRevenue).toFixed(2) : '0.00'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="column has-text-right">
                                <button
                                    className={`button is-info is-small ${downloadingMilkCollections ? 'is-loading' : ''}`}
                                    onClick={downloadMilkCollectionsPDF}
                                    disabled={downloadingMilkCollections}
                                >
                                    <span className="icon">
                                        <i className="fas fa-file-pdf"></i>
                                    </span>
                                    <span>Download Collections</span>
                                </button>
                            </div>
                        </div>
                        <p className="is-size-6 has-text-danger">
                            * This is not the final calculation. It's based on the applied filters and may include already paid amounts. <strong>DO NOT PAY THIS AMOUNT.</strong>
                        </p>
                    </div>
                )}

                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <input type="date" className="input" value={filterDate} onChange={handleFilterDateChange} />
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <input type="month" className="input" value={filterMonth} onChange={handleFilterMonthChange} />
                        </div>
                    </div>
                </div>

                <table className="table is-fullwidth">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Litres</th>
                            <th>Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {milkCollections.length ? milkCollections.map((collection, index) => (
                            <tr key={collection.id}>
                                <td>{(currentPage - 1) * perPage + index + 1}</td>
                                <td>{formatToDayMonthYear(collection.collection_date)} - {collection.collection_shift}</td>
                                <td>{collection.litres}</td>
                                <td>{collection.price_at_sale}</td>
                            </tr>
                        )) : <tr><td colSpan="3">No data found.</td></tr>}
                    </tbody>
                </table>
                {/* Pagination for Milk Collections */}
                <nav className="pagination">
                    <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                </nav>
            </div>

            {/* Debts */}
            <div className="box">
                <h2 className="title is-5">Debts</h2>
                {debts.length > 0 && (
                    <div className="notification is-light is-success">
                        <div className="columns is-vcentered">
                            <div className="column">
                                <strong>Total Due Debt (KES):</strong>
                                <span className="is-size-4 has-text-weight-bold">
                                    &nbsp; {totalDebt ? Number(totalDebt).toFixed(2) : '0.00'}
                                </span>
                            </div>
                            <div className="column has-text-right">
                                <button className={`button is-info is-small  ${downloadingDebts ? 'is-loading' : ''}`} onClick={downloadDebtsPDF}>
                                    <span className="icon">
                                        <i className="fas fa-file-pdf"></i>
                                    </span>
                                    <span>Download Debts</span>
                                </button>
                            </div>
                        </div>
                        <p className="is-size-6 has-text-danger">
                            * This is not the final calculation. It's based on the applied filters. <strong>DO NOT PAY THIS.</strong>
                        </p>
                    </div>
                )}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <input type="date" className="input" value={filterDebtDate} onChange={handleFilterDebtDateChange} />
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <input type="month" className="input" value={filterDebtMonth} onChange={handleFilterDebtMonthChange} />
                        </div>
                    </div>
                </div>
                <table className="table is-fullwidth">
                    <thead>
                        <tr>
                            <th>Date Taken</th>
                            <th>Product</th>
                            <th>Amount</th>
                            <th>Balance Due</th>
                            <th>Status</th>
                            <th>Options</th>
                        </tr>
                    </thead>
                    <tbody>
                        {debts.length ? debts.map(debt => (
                            <tr key={debt.id}>
                                <td>{debt.date_taken}</td>
                                <td>{debt?.product?.name}</td>
                                <td>{debt.amount}</td>
                                <td>{debt.balance_due}</td>
                                <td>{debt.status}</td>
                                <td>  <Link to={`/dashboard/debts/${debt.id}/details`} className="dropdown-item">
                                    <span className="icon">
                                        <i className="fas fa-eye"></i>
                                    </span>
                                    <span>View</span>
                                </Link> </td>
                            </tr>
                        )) : <tr><td colSpan="4">No debts found.</td></tr>}
                    </tbody>
                </table>
                {/* Pagination for Debts */}
                <nav className="pagination">
                    <button onClick={() => setCurrentDebtPage(currentDebtPage - 1)} disabled={currentDebtPage === 1}>Previous</button>
                    <span>Page {currentDebtPage} of {totalDebtPages}</span>
                    <button onClick={() => setCurrentDebtPage(currentDebtPage + 1)} disabled={currentDebtPage === totalDebtPages}>Next</button>
                </nav>
            </div>
        </main>
    );
}

export default FarmerView;
